import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store";
import { deleteMailTemplate, fetchMailTemplateSends } from "../../utils/http/mail-templates";
import { queryClient } from "../../utils/http/queryClient";

function Summary({
  setShowSendTestModal,
  setBulkTestPaths,
}: {
  setShowSendTestModal: Function;
  setBulkTestPaths: Function;
}) {
  const navigate = useNavigate();

  const [showMore, setShowMore] = useState<"drafts" | "live" | "active" | null>(null);
  const [draftsOn, setDraftsOn] = useState<any>();
  const [amountOfDrafts, setAmountOfDrafts] = useState<any>();
  const [, setLiveVersionsOn] = useState<any>();
  const [activeVersionsOn, setActiveVersionsOn] = useState<any>();
  const [liveVersionsNotActiveOn, setLiveVersionsNotActiveOn] = useState<any>();
  const [, setActiveVersionsNotLiveOn] = useState<any>();

  const [selectedTemplates, setSelectedTemplates] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState<string[]>([]);

  const templatesConfig = useAppSelector((state) => state.company.templateSummary.config);
  const templatesMaintained = useAppSelector((state) => state.company.templateSummary.maintained);

  // Get our mailTemplate sends (they should be already fetched by the loader, hence the staleTime)
  const { data: templateSends } = useQuery({
    queryKey: ["mailTemplates", "sends"],
    queryFn: ({ signal }) => fetchMailTemplateSends({ signal }),
    staleTime: 2000,
  });

  useEffect(() => {
    if (!templatesConfig || !templatesMaintained) return;

    const drafts = templatesMaintained.filter((t: any) => t.version == "draft");
    setDraftsOn(drafts);
    setAmountOfDrafts(drafts.length);

    const liveVersions = templatesMaintained.filter((t: any) => t.version == "live" && t.event !== "replacements");
    templatesConfig
      .filter((t: any) => t.same_body_as)
      .forEach((t) => {
        if (
          liveVersions.filter((l) => l.event === t.event && l.action === t.action && l.target === t.target).length === 0
        ) {
          liveVersions.push(t);
        }
      });
    setLiveVersionsOn(liveVersions);

    // The config entries have no version/languages, so resolve the template we would send a test mail for
    // (the draft if there is one, the live version otherwise)
    const activeVersions = templatesConfig
      .filter((t: any) => t.active)
      .map((c: any) => {
        const maintained = templatesMaintained.filter(
          (m: any) => m.event === c.event && m.action === c.action && m.target === c.target,
        );
        const found = maintained.find((m: any) => m.version === "draft") || maintained.find((m: any) => m.version === "live");
        return found ? { ...c, version: found.version, languages: found.languages } : c;
      });
    setActiveVersionsOn(activeVersions);

    let liveNotActive: any = [];
    liveVersions.forEach((t: any) => {
      if (
        activeVersions.filter((c: any) => c.event == t.event && c.action == t.action && c.target == t.target).length ===
        0
      ) {
        liveNotActive.push(t);
      }
    });
    setLiveVersionsNotActiveOn(liveNotActive);

    let activeNotLive: any = [];
    activeVersions.forEach((c: any) => {
      if (
        !c.same_body_as &&
        liveVersions.filter((t: any) => c.event == t.event && c.action == t.action && c.target == t.target).length === 0
      ) {
        activeNotLive.push(c);
      }
    });
    setActiveVersionsNotLiveOn(activeNotLive);
  }, [templatesConfig, templatesMaintained]);

  // Clear the selection whenever we switch between (or close) the lists
  useEffect(() => {
    setSelectedTemplates([]);
    setDeleteErrors([]);
  }, [showMore]);

  // Keep the bulk test mail modal in sync with what is selected here
  useEffect(() => {
    setBulkTestPaths(selectedTemplates);
  }, [selectedTemplates, setBulkTestPaths]);

  const templateKey = (t: any) => `${t.version}-${t.automation}-${t.event}-${t.action}-${t.target}`;

  // Only templates that actually exist (so not the config-only "same body as" entries) can be selected
  const isSelectable = (t: any) => !!t.version && (t.languages?.length || 0) > 0;

  const isSelected = (t: any) => selectedTemplates.some((s) => templateKey(s) === templateKey(t));

  // The list currently shown, limited to the entries we can act upon
  const shownTemplates: any[] = (
    (showMore === "drafts" && draftsOn) ||
    (showMore === "live" && liveVersionsNotActiveOn) ||
    (showMore === "active" && activeVersionsOn) ||
    []
  ).filter(isSelectable);

  const allShownSelected = shownTemplates.length > 0 && shownTemplates.every((t) => isSelected(t));

  const toggleSelectAll = () => {
    setSelectedTemplates(allShownSelected ? [] : shownTemplates);
  };

  // The word that has to be typed to confirm the deletion (the version we are about to delete)
  const selectedVersions = [...new Set(selectedTemplates.map((t) => t.version))];
  const deleteConfirmationWord = selectedVersions.length === 1 ? selectedVersions[0] : "delete";

  const toggleSelected = (t: any) => {
    setSelectedTemplates((selected) =>
      selected.some((s) => templateKey(s) === templateKey(t))
        ? selected.filter((s) => templateKey(s) !== templateKey(t))
        : [...selected, t],
    );
  };

  const deleteSelectedTemplates = async () => {
    setDeleting(true);
    const newErrors: string[] = [];

    // One delete per template and language, fired in batches so we don't wait for each one separately
    const deletions = selectedTemplates.flatMap((t: any) =>
      t.languages.map((language: string) => async () => {
        try {
          await deleteMailTemplate({
            mailTemplatePath: {
              language,
              event: t.event,
              action: t.action,
              target: t.target,
              version: t.version,
            },
          });
        } catch (e: any) {
          newErrors.push(
            `${t.automation} > ${t.event} > ${t.action} > ${t.target} (${language}): ${e.info || e.message}`,
          );
        }
      }),
    );

    const batchSize = 10;
    for (let i = 0; i < deletions.length; i += batchSize) {
      await Promise.all(deletions.slice(i, i + batchSize).map((deletion: any) => deletion()));
    }

    await queryClient.invalidateQueries({ queryKey: ["mailTemplates"] });

    setDeleting(false);
    setShowDeleteModal(false);
    setDeleteConfirmation("");
    setSelectedTemplates([]);
    setDeleteErrors(newErrors);
  };

  const renderCheckbox = (t: any) => (
    <Form.Check
      inline
      className="me-2"
      checked={isSelected(t)}
      onClick={(e) => e.stopPropagation()}
      onChange={() => toggleSelected(t)}
    />
  );

  return (
    <Alert variant="info">
      You have {amountOfDrafts == 0 && "no drafts"}
      {amountOfDrafts > 0 && (
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            setShowMore(showMore === "drafts" ? null : "drafts");
          }}
        >
          {amountOfDrafts} draft(s)
        </Link>
      )}
      . {activeVersionsOn && activeVersionsOn.length == 0 && "No flows"}{" "}
      {activeVersionsOn && activeVersionsOn.length > 0 && (
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            setShowMore(showMore === "active" ? null : "active");
          }}
        >
          {activeVersionsOn.length} flow(s)
        </Link>
      )}{" "}
      is/are set to active.{" "}
      {liveVersionsNotActiveOn && liveVersionsNotActiveOn.length == 0 && "All live version(s) are active."}
      {liveVersionsNotActiveOn && liveVersionsNotActiveOn.length > 0 && (
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            setShowMore(showMore === "live" ? null : "live");
          }}
        >
          {liveVersionsNotActiveOn.length} live version(s) is/are not active.
        </Link>
      )}
      {showMore && (
        <Container>
          <Row>
            <Col xs={9} style={{ fontSize: "14px" }}>
              {shownTemplates.length > 0 && (
                <div className="pt-3">
                  <Form.Check
                    inline
                    checked={allShownSelected}
                    onChange={toggleSelectAll}
                    label={allShownSelected ? "deselect all" : "select all"}
                  />
                  {selectedTemplates.length > 0 && <span>({selectedTemplates.length} selected)</span>}
                </div>
              )}
              {showMore === "drafts" && (
                <ul className="pt-3">
                  {draftsOn?.map((t: any) => {
                    return (
                      <li
                        key={`draftsOn-${t.automation}${t.event}${t.action}${t.target}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setShowMore(null);
                          navigate(`/mail-templates/nl/${t.automation}/${t.event}/${t.action}/${t.target}/draft`);
                        }}
                      >
                        {isSelectable(t) && renderCheckbox(t)}
                        {t.automation} {">"} {t.event} {">"} {t.action} {">"} {t.target}
                      </li>
                    );
                  })}
                </ul>
              )}
              {showMore === "live" && (
                <ul className="pt-3">
                  {liveVersionsNotActiveOn?.map((t: any) => {
                    return (
                      <li
                        key={`liveVersionsOn-${t.automation}${t.event}${t.action}${t.target}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setShowMore(null);
                          navigate(`/mail-templates/nl/${t.automation}/${t.event}/${t.action}/${t.target}/live`);
                        }}
                      >
                        {isSelectable(t) && renderCheckbox(t)}
                        {t.automation} {">"} {t.event} {">"} {t.action} {">"} {t.target}
                      </li>
                    );
                  })}
                </ul>
              )}
              {showMore === "active" && (
                <>
                  <div className="pt-3" style={{ fontSize: "12px" }}>
                    {!templateSends && (
                      <>
                        <Spinner animation="border" size="sm" />
                        &nbsp;&nbsp;&nbsp;
                      </>
                    )}
                    A red dot indicates the mail is active but has never been sent in the past year.
                  </div>
                  <ul className="pt-3">
                    {activeVersionsOn?.map((t: any) => {
                      let key = `${t.event}__${t.action}__${t.target}`;
                      if (t.event === "INVALID_DATA") {
                        key = "INVALID_DATA";
                      }
                      return (
                        <li
                          key={`active-${t.automation}${t.event}${t.action}${t.target}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setShowMore(null);
                            navigate(`/mail-templates/nl/${t.automation}/${t.event}/${t.action}/${t.target}/draft`);
                          }}
                        >
                          {isSelectable(t) && renderCheckbox(t)}
                          {templateSends && !(key in templateSends) && (
                            <i className="ps-1 bi bi-circle-fill pe-2" style={{ color: "#FF0000" }}></i>
                          )}
                          {t.automation} {">"} {t.event} {">"} {t.action} {">"} {t.target}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {showMore && (draftsOn?.length || 0) > 0 && (
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowMore(null);
                  }}
                >
                  show less
                </Link>
              )}
            </Col>
            <Col xs={3} className="text-end">
              <Button
                className="p-2 m-1 btn-sm"
                onClick={() => setShowSendTestModal("BULK")}
                disabled={selectedTemplates.length === 0}
              >
                Bulk Send Test Mails
              </Button>
              {(showMore === "drafts" || showMore === "live") && (
                <div>
                  <Button
                    variant="danger"
                    className="p-2 m-1 btn-sm"
                    onClick={() => {
                      setDeleteConfirmation("");
                      setShowDeleteModal(true);
                    }}
                    disabled={selectedTemplates.length === 0}
                  >
                    Delete templates
                  </Button>
                </div>
              )}
            </Col>
          </Row>
          {deleteErrors.length > 0 && (
            <Row>
              <Col>
                <Alert variant="danger" className="mt-2" style={{ fontSize: "14px" }}>
                  {deleteErrors.map((e) => (
                    <div key={e}>{e}</div>
                  ))}
                </Alert>
              </Col>
            </Row>
          )}
        </Container>
      )}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Delete templates</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete these {selectedTemplates.length} template(s)? This cannot be undone.</p>
          <ul style={{ fontSize: "12px" }}>
            {selectedTemplates.map((t) => (
              <li key={templateKey(t)}>
                {t.automation} {">"} {t.event} {">"} {t.action} {">"} {t.target} ({t.version})
              </li>
            ))}
          </ul>
          <p>
            Type <b>{deleteConfirmationWord}</b> to confirm:
          </p>
          <Form.Control
            autoFocus
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            placeholder={deleteConfirmationWord}
            disabled={deleting}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={deleteSelectedTemplates}
            disabled={deleting || deleteConfirmation !== deleteConfirmationWord}
          >
            {deleting && (
              <>
                <Spinner animation="border" size="sm" />
                &nbsp;&nbsp;Deleting...
              </>
            )}
            {!deleting && "Delete templates"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Alert>
  );
}

export default Summary;
